import { IMAGES } from "./company";
import { SERVICE_INDEX } from "./services-index";
import { assertIndexInSync } from "./index-sync";

// Full service content: hero, long-form copy, signs, process, FAQs.
// Icons reference lucide-react icon names, resolved in components.

export const SERVICES = [
  {
    slug: "emergency-plumbing",
    name: "Emergency Plumbing (24/7)",
    shortName: "Emergency Plumbing",
    icon: "Siren",
    image: IMAGES.brandEmergencyNight,
    metaTitle: "24/7 Emergency Plumber in Washington | IDB Plumbing",
    metaDescription:
      "Burst pipe, sewage backup, or no water? IDB Plumbing Solutions answers 24/7 with crews in Federal Way, Vancouver, and Spokane. Licensed, bonded, insured.",
    excerpt:
      "Burst pipes, backups, and failures don't wait for business hours. Our on-call crews answer 24/7 across Washington.",
    intro: [
      "A plumbing emergency is a race against water. Every minute a burst pipe runs, it pushes water into framing, insulation, and flooring — and the repair bill grows with it. That is why IDB Plumbing Solutions staffs a live emergency line 24 hours a day, 365 days a year. When you call, you talk to a dispatcher, not a machine. They help you stop the immediate damage, then send the nearest on-call crew from our Federal Way, Vancouver, or Spokane office.",
      "Our emergency plumbers arrive in stocked trucks. That matters more than it sounds: most after-hours failures — burst supply lines, failed water heaters, broken shutoff valves, sewage backups — can be permanently repaired on the first visit because the parts are already on board. When a full repair has to wait for daylight or a special-order part, we stabilize the situation properly: isolate the line, cap the leak, protect the area, and give you a clear written plan for the permanent fix.",
      "Emergency pricing is straightforward. The dispatcher quotes the after-hours dispatch fee on the phone before a truck rolls. Once the technician diagnoses the problem, you get a flat-rate price for the repair — the same rate book we use during the day — and you approve it before any work begins. No hourly meter running while you watch, no invoice surprises the morning after.",
      "We handle emergency calls in homes, rental properties, restaurants, and commercial buildings across Washington State. Because our dispatcher tracks every crew in real time, you get an honest arrival window whether you're in South King County, Clark County, or the Spokane area. Water damage doesn't negotiate, and neither do we: fast arrival, honest diagnosis, permanent repair, and a workmanship warranty on the fix.",
    ],
    signs: [
      "Water is leaking and you can't stop it at a fixture valve",
      "Sewage is backing up into tubs, showers, or floor drains",
      "You have no water anywhere in the building",
      "The water heater is leaking from the tank itself",
      "A pipe has frozen and you suspect it has split",
      "You smell gas — leave first, call your utility, then call us",
    ],
    process: [
      {
        title: "Call the 24/7 line",
        text: "A live dispatcher answers, quotes the dispatch fee, and helps you shut off water to limit damage while the crew is en route.",
      },
      {
        title: "Fast local dispatch",
        text: "The nearest on-call crew rolls from Federal Way, Vancouver, or Spokane with a stocked truck and an honest arrival window.",
      },
      {
        title: "Diagnose and quote",
        text: "The technician finds the true source of the failure and gives you a flat-rate price. You approve it before any work starts.",
      },
      {
        title: "Repair and warranty",
        text: "Most emergencies are permanently fixed on the first visit. Every repair carries our written workmanship warranty.",
      },
    ],
    faqs: [
      {
        q: "How fast can you get here?",
        a: "It depends on your location and the time of night, but our on-call crews cover Washington around the clock and the dispatcher knows exactly where each one is. The dispatcher gives you a realistic arrival window when you call — and we keep you posted if it changes.",
      },
      {
        q: "What does an emergency call cost?",
        a: "You'll hear the after-hours dispatch fee on the phone before we send a truck. The repair itself is quoted flat-rate on site, from the same price book we use during business hours, and you approve it before work begins.",
      },
      {
        q: "Should I shut off my water before you arrive?",
        a: "Yes, if you can. Close the valve nearest the leak, or the main shutoff if the leak is bigger than one fixture. Our dispatcher will talk you through finding it. Shutting off early can save thousands in water damage.",
      },
      {
        q: "Do you handle commercial emergencies too?",
        a: "Yes. Restaurants, offices, retail spaces, and multi-family buildings are a regular part of our emergency work. We understand that downtime costs you money, and we prioritize getting your business operational.",
      },
      {
        q: "Is the emergency repair permanent or temporary?",
        a: "Permanent whenever possible — our trucks carry the parts for the most common failures. If a full repair genuinely has to wait, we stabilize the system safely and give you a written plan and price for the permanent fix.",
      },
    ],
  },
  {
    slug: "drain-cleaning-sewer",
    name: "Drain Cleaning & Sewer Services",
    shortName: "Drain & Sewer",
    icon: "Waves",
    image: IMAGES.brandDrainService,
    metaTitle: "Drain Cleaning & Sewer Service in Washington | IDB Plumbing",
    metaDescription:
      "Hydro jetting, camera inspections, and honest sewer diagnostics across Washington. IDB clears drains right the first time from Federal Way, Vancouver & Spokane.",
    excerpt:
      "From slow kitchen sinks to full sewer backups, we clear the line and show you the proof — camera footage included.",
    intro: [
      "A slow drain is your plumbing system telling you something. Grease, scale, roots, or a bellied line — the cause matters, because the fix that lasts depends on it. IDB Plumbing Solutions doesn't just punch a hole through the clog and drive away. We clear the line, run a camera when the situation calls for it, and show you exactly what's happening inside your pipes so you can make an informed decision.",
      "For routine clogs, our technicians use professional-grade cable machines sized to your line — not the undersized rigs from a rental counter that punch through the middle of a blockage and leave the walls of the pipe coated. For grease-choked kitchen lines, root intrusion, and years of accumulated scale, we bring hydro jetting: high-pressure water that scours the pipe wall back to its original diameter. It is the difference between trimming a hedge and pulling the roots.",
      "Our sewer camera inspections take the guesswork out of underground problems. A high-resolution camera travels the full length of your sewer lateral while we record footage and mark the location and depth of every defect — root balls, offsets, bellies, cracks, and collapsed sections. You get the recording and a plain-language explanation of what we found. If the line is healthy, we tell you that too. If it needs repair, you'll see the evidence before you spend a dollar.",
      "Homebuyers across Washington use our pre-purchase sewer scopes to avoid five-figure surprises, and restaurants rely on our scheduled jetting programs to stay ahead of health inspections. Whatever brings you to us — a gurgling toilet in Federal Way, a backed-up floor drain in Vancouver, or a root-invaded lateral in Spokane — we'll clear it, prove it, and stand behind the work.",
    ],
    signs: [
      "Multiple fixtures drain slowly or gurgle at the same time",
      "Sewage odors from drains, the yard, or the basement",
      "Water backs up in the tub or shower when the toilet flushes",
      "Recurring clogs in the same line despite repeated clearing",
      "Unusually lush, wet patches of lawn over the sewer line",
      "You're buying an older home and the sewer has never been scoped",
    ],
    process: [
      {
        title: "Locate the blockage",
        text: "We identify which line is affected and choose the right access point — cleanout, roof vent, or fixture — to protect your home.",
      },
      {
        title: "Clear the line",
        text: "Cable machine for straightforward clogs; hydro jetting for grease, roots, and scale that cabling can't fully remove.",
      },
      {
        title: "Verify with camera",
        text: "When warranted, we run a camera to confirm the line is clean and document any defects, with footage you keep.",
      },
      {
        title: "Prevent the repeat",
        text: "You get a straight answer about why it happened and what — if anything — is worth doing to keep it from happening again.",
      },
    ],
    faqs: [
      {
        q: "What is hydro jetting and do I need it?",
        a: "Hydro jetting uses high-pressure water to scour the full inside wall of the pipe, removing grease, roots, and scale that a cable just pokes through. You need it when clogs keep coming back or a camera shows heavy buildup. For a simple one-time clog, cabling is usually enough — and we'll tell you which is which.",
      },
      {
        q: "How much does a sewer camera inspection cost?",
        a: "We charge a flat diagnostic fee, quoted before we start, and we credit it toward the repair if you hire us for the work. You keep the footage either way.",
      },
      {
        q: "Can hydro jetting damage old pipes?",
        a: "Not when it's done properly. We adjust pressure to the pipe material and condition, and we camera-inspect questionable lines first. A line so fragile that jetting would harm it is a line that already needs repair — and you deserve to know that.",
      },
      {
        q: "Why does my drain keep clogging in the same spot?",
        a: "Recurring clogs almost always mean a physical defect: a root intrusion, a sag (belly) holding water, or a pipe offset catching debris. A camera inspection finds the exact cause and depth so you can fix the problem instead of renting it.",
      },
      {
        q: "Do you offer scheduled maintenance for restaurants?",
        a: "Yes. We run quarterly and semi-annual jetting programs for restaurants and commercial kitchens across Washington, with documentation you can show your landlord or health inspector.",
      },
    ],
  },
  {
    slug: "water-heaters",
    name: "Water Heater Installation & Repair",
    shortName: "Water Heaters",
    icon: "Flame",
    image: IMAGES.brandWaterHeaterInstall,
    financing: true,
    metaTitle: "Water Heater Installation & Repair in WA | IDB Plumbing",
    metaDescription:
      "Tank and tankless water heater repair, replacement, and installation across Washington State. Same-day service from Federal Way, Vancouver, and Spokane.",
    excerpt:
      "Tank or tankless, gas or electric — we repair what's fixable and install what's right, usually the same day.",
    intro: [
      "No hot water has a way of reorganizing your whole day. IDB Plumbing Solutions repairs and replaces water heaters across Washington State, and in most cases we can restore your hot water the same day you call. Our trucks carry common replacement units and parts, and our plumbers are trained on every configuration you'll find in a Washington home: gas and electric tanks, condensing and non-condensing tankless, heat pump hybrids, and everything in between.",
      "We start with an honest diagnosis. A leaking tank is done — no repair changes that — but a failed heating element, thermostat, thermocouple, or gas valve is often a modest fix that buys years of service. If your heater is under ten years old and the tank itself is sound, we'll usually recommend the repair. If it's older, rusting, or failing in a way that will repeat, we'll show you replacement options at more than one price point and explain the real differences in plain language.",
      "Thinking about going tankless? We install more of them every year, and for good reason: endless hot water, lower standby energy loss, and a footprint the size of a suitcase. But tankless isn't automatic. Your gas line size, venting path, and water hardness all affect whether it's the right call, and a proper installation often includes a gas line upgrade and condensate handling. We assess all of it up front and quote the complete job — not a teaser price that grows on installation day.",
      "Every installation is permitted where required, installed to Washington code — including seismic strapping, expansion tanks, and proper pan and drain configuration — and registered for its full manufacturer warranty. Our workmanship warranty is on top of that, in writing. Hot water is one of those things you shouldn't have to think about. Let's get it that way again.",
    ],
    signs: [
      "Water is lukewarm, runs out fast, or takes ages to recover",
      "Rust-colored water from the hot side only",
      "Rumbling or popping sounds from the tank (sediment buildup)",
      "Moisture, drips, or corrosion around the base of the tank",
      "The heater is over 10 years old and repairs are adding up",
      "Pilot light won't stay lit, or the breaker keeps tripping",
    ],
    process: [
      {
        title: "Diagnose honestly",
        text: "We test elements, thermostats, gas valves, and the tank itself — and tell you plainly whether repair or replacement is the smarter spend.",
      },
      {
        title: "Quote your options",
        text: "Flat-rate pricing on repair and on replacement at more than one quality level, so you choose with full information.",
      },
      {
        title: "Install to code",
        text: "Permits, seismic strapping, expansion tank, venting, and drainage — done right, inspected, and registered for warranty.",
      },
      {
        title: "Test and walk through",
        text: "We verify temperature and operation, haul away the old unit, and show you the shutoffs and settings before we leave.",
      },
    ],
    faqs: [
      {
        q: "Tank or tankless — which should I choose?",
        a: "Tankless wins on endless hot water, efficiency, and lifespan; a quality tank wins on upfront cost and simplicity. The right answer depends on your household's hot water use, your gas service, and your budget. We quote both honestly and let you decide — see our blog comparison for a deeper dive.",
      },
      {
        q: "How long does a water heater installation take?",
        a: "A like-for-like tank swap typically takes 2–3 hours. A tankless conversion — with gas line upgrade and new venting — is usually a full day. We confirm the timeline before we start.",
      },
      {
        q: "How long should a water heater last?",
        a: "Storage tanks typically last 8–12 years in Washington water conditions; tankless units 15–20 with routine descaling. Anode rod replacement and periodic flushing can meaningfully extend tank life — ask us about maintenance when we're out.",
      },
      {
        q: "Do I need a permit to replace a water heater?",
        a: "In most Washington jurisdictions, yes. We handle the permit and the inspection as part of the job. It protects your warranty, your insurance coverage, and your resale paperwork.",
      },
      {
        q: "My water heater is leaking — is that an emergency?",
        a: "If the tank itself is leaking, yes — it will not heal, and it can let go entirely. Shut off the cold supply valve above the heater and the gas or breaker, then call our 24/7 line. We'll get you scheduled fast.",
      },
    ],
  },
  {
    slug: "repiping-pipe-repair",
    name: "Repiping & Pipe Repair",
    shortName: "Repiping",
    icon: "Wrench",
    image: IMAGES.brandRepipeInterior,
    financing: true,
    metaTitle: "Repiping & Pipe Repair in WA | Copper & PEX | IDB Plumbing",
    metaDescription:
      "Whole-home copper and PEX repipes, slab leak repair, and pipe replacement across Washington. Clean, fast, warrantied work from IDB Plumbing Solutions.",
    excerpt:
      "Failing galvanized or polybutylene pipes? We repipe whole homes in copper or PEX — cleanly, quickly, and with a long warranty.",
    intro: [
      "Pipes age out. Galvanized steel rusts shut from the inside, polybutylene becomes brittle and fails without warning, and even copper can pit in aggressive water. If your home was built before the mid-1990s and still has its original supply piping, the question usually isn't whether it will fail — it's when, and how much damage it does on the way. IDB Plumbing Solutions repipes homes across Washington State with modern copper or PEX systems, engineered and installed to last for generations.",
      "A repipe sounds disruptive. Done right, it isn't. Our crews repipe most single-family homes in two to four days. We protect floors and furniture, make precise access openings instead of tearing out walls, keep water on each evening whenever possible, and patch every opening ready for paint. You'll know the schedule before we start, and the house gets cleaned before we hand it back. Our repipe customers routinely tell us the job was far less painful than they feared — that is by design.",
      "Copper or PEX? Both are excellent when installed correctly, and we install both. Type L copper is time-proven, rigid, and unaffected by UV — the traditional premium choice. PEX-A is flexible, freeze-tolerant, quieter, faster to install, and typically more affordable, with fewer joints hidden in walls. We'll walk you through the honest trade-offs for your specific house and water rather than pushing whichever is convenient for us.",
      "Not every pipe problem needs a full repipe, and we'll tell you when it doesn't. Slab leaks can often be rerouted overhead without breaking concrete. A single failed section can be repaired properly with the right materials and permits. But when repairs are stacking up and your water runs rusty every morning, replacement is the honest recommendation — and we back ours with one of the strongest repipe warranties in the state.",
    ],
    signs: [
      "Rusty or discolored water, especially first thing in the morning",
      "Low water pressure that's worse when two fixtures run",
      "Repeated pinhole leaks in different parts of the house",
      "Visible corrosion on exposed pipes in the crawl space or garage",
      "Your home has galvanized steel or polybutylene piping",
      "Warm floor spots or unexplained spikes in your water bill (slab leak)",
    ],
    process: [
      {
        title: "Evaluate and plan",
        text: "We inspect your existing system, map every fixture, and design the new pipe routes to minimize wall openings.",
      },
      {
        title: "Firm written quote",
        text: "One itemized price covering pipe, valves, permits, patching, and cleanup. Copper and PEX options quoted side by side.",
      },
      {
        title: "Repipe in days",
        text: "Floors protected, precise access cuts, water restored each evening whenever possible. Most homes take 2–4 days.",
      },
      {
        title: "Inspect, patch, warranty",
        text: "The system is pressure-tested and inspected, openings are patched ready for paint, and your warranty is delivered in writing.",
      },
    ],
    faqs: [
      {
        q: "How much does a whole-home repipe cost?",
        a: "It depends on the home's size, number of fixtures, access, and material choice — PEX typically costs less than copper. After a walkthrough we give you a firm, itemized quote for both options where practical. No per-foot surprises.",
      },
      {
        q: "Will I have water during the repipe?",
        a: "In most projects, yes — we work in stages and restore water each evening. Any planned outage windows are communicated in advance so you can plan around them.",
      },
      {
        q: "Is PEX as good as copper?",
        a: "Modern PEX-A is an outstanding material: freeze-tolerant, corrosion-proof, quiet, and installed with fewer in-wall joints. Copper remains the rigid, time-proven premium option. We install both to manufacturer spec and warranty both — the right choice depends on your house and priorities.",
      },
      {
        q: "Do you patch the drywall afterward?",
        a: "Yes. Our quotes include patching access openings smooth and ready for paint. We're not the crew that leaves your hallway looking like Swiss cheese.",
      },
      {
        q: "Can you fix a slab leak without jackhammering the floor?",
        a: "Usually. Our preferred approach is rerouting the affected line overhead through walls or the attic, which avoids breaking concrete entirely and makes the new line accessible for the future. We only open a slab when rerouting genuinely isn't viable.",
      },
    ],
  },
  {
    slug: "fixture-installation",
    name: "Fixture Installation & Bathroom/Kitchen Plumbing",
    shortName: "Fixtures & Remodels",
    icon: "ShowerHead",
    image: IMAGES.brandFixtureInstall,
    metaTitle: "Fixture Installation & Bath Plumbing in WA | IDB Plumbing",
    metaDescription:
      "Faucets, toilets, sinks, showers, and full remodel plumbing installed right across Washington State. Clean workmanship from IDB Plumbing Solutions.",
    excerpt:
      "Faucets, toilets, sinks, and full remodel rough-ins — installed level, sealed right, and warrantied in writing.",
    intro: [
      "Fixtures are where you actually touch your plumbing every day, and the quality of the installation is what separates a faucet that feels solid for fifteen years from one that wobbles in six months. IDB Plumbing Solutions installs kitchen and bathroom fixtures across Washington State — faucets, sinks, toilets, showers, tubs, garbage disposals, pot fillers, and bidet seats — with the kind of care that makes the hardware feel like part of the house.",
      "Bring your own fixture or let us supply it; we install both. If you're shopping, we'll tell you frankly which brands we see fail and which ones we'd put in our own homes. Every installation includes new supply lines and shutoff valves where the old ones are suspect — because reusing a corroded 20-year-old angle stop under a brand-new faucet is how floors get ruined. Small details like that are the difference between installed and installed right.",
      "Remodeling a kitchen or bathroom? We handle the full plumbing scope: demolition-phase disconnects, rough-in for new layouts, moving drains and supplies for that relocated island sink or curbless shower, and finish installation once tile and cabinets are in. We work directly with your general contractor or with you as an owner-builder, we show up when the schedule says we will, and our rough-ins pass inspection — which keeps your whole project on time.",
      "We also solve the small-but-maddening problems: the toilet that rocks, the faucet that drips at 2 AM, the disposal that hums but won't spin, the shower that can't hold temperature. These calls get the same flat-rate pricing and the same written warranty as the big jobs. If it carries water in your kitchen or bath, we install it, repair it, and stand behind it.",
    ],
    signs: [
      "A faucet drips, wobbles, or has lost pressure at the spout",
      "The toilet rocks, runs constantly, or needs double flushes",
      "Water stains inside the sink cabinet or around the toilet base",
      "You're remodeling and need drains or supplies moved",
      "Your shower can't hold a steady temperature",
      "Fixtures are dated, corroded, or perpetually hard to clean",
    ],
    process: [
      {
        title: "Confirm the scope",
        text: "We review your fixtures — yours or ours — check valve and supply condition, and flag anything that should be replaced while the work is open.",
      },
      {
        title: "Flat-rate quote",
        text: "One clear price for the complete installation, including new supply lines and stops where needed. Approved before we start.",
      },
      {
        title: "Install with care",
        text: "Surfaces protected, connections torqued and sealed to spec, fixtures set level and square. We test everything under pressure.",
      },
      {
        title: "Clean up and warranty",
        text: "Old fixtures hauled away, work area left clean, and your written workmanship warranty in hand.",
      },
    ],
    faqs: [
      {
        q: "Can I buy my own fixtures and have you install them?",
        a: "Absolutely — many customers do. We'll flag any known reliability issues with the model before we install it, and our workmanship warranty covers the installation either way.",
      },
      {
        q: "How long does a faucet or toilet installation take?",
        a: "Most single-fixture installations take one to two hours. If we find corroded shutoff valves or supply lines, replacing them adds a little time — and a lot of future peace of mind.",
      },
      {
        q: "Do you do full bathroom remodels?",
        a: "We handle the complete plumbing scope of a remodel — demo disconnects, rough-in, and finish installation — working alongside your contractor or directly with you. Tile, electrical, and cabinetry come from your other trades.",
      },
      {
        q: "Why does my new faucet have low pressure?",
        a: "Usually debris in the aerator or a partially closed supply stop; occasionally a failing cartridge or, in older homes, corroded galvanized supply lines restricting flow. We diagnose the actual cause instead of guessing.",
      },
      {
        q: "Is a running toilet worth fixing right away?",
        a: "Yes. A running toilet can quietly waste hundreds of gallons per day — you'll see it on your utility bill before you hear it. It's typically a fast, inexpensive repair.",
      },
    ],
  },
  {
    slug: "water-sewer-line-replacement",
    name: "Water & Sewer Line Replacement",
    shortName: "Water & Sewer Lines",
    icon: "Shovel",
    image: IMAGES.brandTruckJobsite,
    financing: true,
    metaTitle: "Water & Sewer Line Replacement in WA | IDB Plumbing",
    metaDescription:
      "Trenchless and conventional water and sewer line replacement across Washington. Camera-verified diagnosis, honest quotes, lasting repairs from IDB Plumbing.",
    excerpt:
      "Broken main or failing sewer lateral? Trenchless technology lets us replace most lines without destroying your yard.",
    intro: [
      "The pipes buried between your house and the street do the hardest work in your plumbing system, and when they fail, the symptoms show up in unpleasant ways: soggy trenches in the lawn, sewage backing into the house, or a water bill that suddenly doubles. IDB Plumbing Solutions replaces water services and sewer laterals across Washington State — and in most cases, we do it without excavating a trench through everything you've planted.",
      "Trenchless replacement changed this work fundamentally. With pipe bursting, we pull a new high-density polyethylene pipe through the path of the old one, fracturing the failed pipe outward as the new one takes its place — one entry pit, one exit pit, and your yard, driveway, and mature trees stay intact. With cured-in-place lining, we can rehabilitate structurally sound pipe with a new jointless liner inside the old host. Both methods produce a line with a service life measured in generations, and both are typically finished in a day or two.",
      "Every underground job starts with evidence, not guesswork. We camera the line, locate defects by depth and position, and show you the footage. If a spot repair will genuinely solve the problem, we quote the spot repair — replacing an entire lateral to fix one root intrusion is the kind of overselling we built this company to compete against. When full replacement is warranted, you get an itemized quote covering everything: permits, utility locates, excavation, restoration, and inspection.",
      "We coordinate the whole process, including city and county permits, right-of-way work where the lateral crosses public property, and connection inspections. Conventional open-trench replacement remains the right tool in some situations — shallow lines, severe collapses, or lines with major grade problems — and we're straightforward about when that's the case. Either way, the line gets replaced once, correctly, with a warranty behind it.",
    ],
    signs: [
      "Sewage backups that return even after the line is cleared",
      "Persistent wet, sunken, or unusually green strips in the yard",
      "A sudden, unexplained jump in your water bill",
      "Low water pressure throughout the entire house",
      "Foundation-area dampness or the smell of sewage outdoors",
      "Your home is 40+ years old with original concrete or clay pipes",
    ],
    process: [
      {
        title: "Camera and locate",
        text: "We inspect the full line, record the footage, and mark every defect by depth and position. You see exactly what we see.",
      },
      {
        title: "Quote every option",
        text: "Spot repair, lining, pipe bursting, or open trench — each priced and explained honestly, including permits and restoration.",
      },
      {
        title: "Replace the line",
        text: "Most trenchless jobs finish in one to two days with minimal digging. We handle locates, permits, and inspections throughout.",
      },
      {
        title: "Verify and restore",
        text: "The new line is inspected and camera-verified, pits are backfilled and compacted, and the site is restored.",
      },
    ],
    faqs: [
      {
        q: "What does trenchless sewer replacement cost?",
        a: "Typically comparable to or moderately more than open-trench on the pipe work itself — but dramatically less once you count landscape, driveway, and hardscape restoration that trenching destroys. We quote both methods so you can compare the full picture.",
      },
      {
        q: "How long does the work take?",
        a: "Most residential trenchless replacements are completed in one to two days, including the entry and exit pits. Conventional excavation typically runs two to four days depending on depth and length.",
      },
      {
        q: "Will my yard really survive?",
        a: "With trenchless, yes — we dig an entry pit and an exit pit, typically a few feet square each, instead of a trench across the whole yard. Lawns, gardens, fences, and driveways above the pipe path stay untouched.",
      },
      {
        q: "Who is responsible for the sewer lateral — me or the city?",
        a: "In most Washington jurisdictions, the homeowner owns the lateral from the house to the public main, including the section under the street. We confirm the exact rules with your city or district as part of the job.",
      },
      {
        q: "Can every pipe be replaced trenchlessly?",
        a: "Most, but not all. Fully collapsed pipes, severe grade (slope) problems, and certain tie-in configurations still call for open excavation. The camera inspection tells us definitively, and we'll show you why either way.",
      },
    ],
  },
  {
    slug: "gas-lines",
    name: "Gas Line Installation & Repair",
    shortName: "Gas Lines",
    icon: "FlameKindling",
    image: IMAGES.brandGasLineWork,
    metaTitle: "Gas Line Installation & Repair in Washington | IDB Plumbing",
    metaDescription:
      "Licensed gas piping installation, repair, and leak response across Washington State. Ranges, fireplaces, BBQs, generators, and tankless upgrades. IDB Plumbing.",
    excerpt:
      "New gas runs for ranges, fire pits, generators, and tankless heaters — permitted, pressure-tested, and done by licensed pros.",
    intro: [
      "Gas work is not a place for shortcuts. Every gas line we install or repair is sized by load calculation, joined to manufacturer specification, pressure-tested beyond code requirements, and inspected under permit. IDB Plumbing Solutions is licensed for gas piping across Washington State, and our plumbers treat every connection with the seriousness the fuel deserves — because 'probably fine' is not a standard anyone should accept in their home.",
      "Most of our gas work is about adding capability: a new line for the gas range you've always wanted, a stub-out for the patio BBQ or fire pit, fuel for a standby generator, or upsizing supply to feed a tankless water heater conversion. Proper sizing is the invisible part that matters most — an undersized line starves appliances at peak demand, causing nuisance shutdowns and, in the worst cases, incomplete combustion. We calculate the full connected load, measure the run, and size the pipe so everything burns clean at full fire.",
      "We also repair and replace aging gas systems. Older homes across Washington still carry decades-old black iron with corroded joints, and flexible CSST installed before bonding requirements took effect. If you smell gas, your first steps are always the same: leave the building, don't operate switches, and call your utility's emergency line from outside. Once the utility makes the situation safe, we perform the diagnosis and permanent repair — leak-located, pressure-verified, and inspected.",
      "Every project ends the same way: a documented pressure test held longer than code demands, a permit inspection passed, and appliance connections leak-checked at operating pressure. Gas done right is completely uneventful — and uneventful is exactly what we deliver.",
    ],
    signs: [
      "You smell gas — leave immediately, then call your utility, then us",
      "A hissing sound near a gas line or appliance connection",
      "Dead vegetation in a strip over a buried gas line",
      "You're adding a gas range, dryer, fireplace, fire pit, or generator",
      "Your tankless water heater upgrade needs a larger supply line",
      "Aging black iron pipe with visible rust at the joints",
    ],
    process: [
      {
        title: "Load calculation",
        text: "We total the BTU demand of every current and planned appliance and design the line size and route accordingly.",
      },
      {
        title: "Permit and install",
        text: "Permitted installation with materials matched to the environment — black iron, CSST with proper bonding, or PE for buried runs.",
      },
      {
        title: "Pressure test",
        text: "The system is pressure-tested beyond code requirements and holds before any gas flows. Documented, every time.",
      },
      {
        title: "Inspect and connect",
        text: "The permit inspection is passed, appliances are connected and leak-checked at operating pressure, and you get the paperwork.",
      },
    ],
    faqs: [
      {
        q: "I smell gas. What do I do first?",
        a: "Leave the building immediately — don't flip switches, light anything, or use a phone inside. From outside, call your gas utility's emergency line; they respond around the clock and make the scene safe. Then call us for the diagnosis and permanent repair.",
      },
      {
        q: "Can you run a gas line to my kitchen for a new range?",
        a: "Yes — it's one of our most common projects. We verify your meter and existing piping can carry the added load, run the new line, pressure-test it, and coordinate the inspection. Most kitchen runs are completed in a day.",
      },
      {
        q: "Do gas lines require permits?",
        a: "Yes, in virtually every Washington jurisdiction. We pull the permit and schedule the inspection as part of every gas job — it's non-negotiable for safety and required for insurance and resale.",
      },
      {
        q: "What's the difference between black iron and CSST?",
        a: "Black iron is rigid threaded steel pipe — durable and time-proven. CSST is flexible stainless tubing that installs faster in tight spaces but must be properly bonded to the electrical grounding system. We use each where it's the right fit and always to manufacturer spec.",
      },
      {
        q: "Can you install the line for an outdoor fire pit or BBQ?",
        a: "Yes. We run buried polyethylene or properly protected line to patios and outdoor kitchens, with a shutoff valve at the appliance and full pressure testing. Winter-proof and inspected.",
      },
    ],
  },
  {
    slug: "commercial-new-construction",
    name: "New Construction & Commercial Plumbing",
    shortName: "Commercial",
    icon: "Building2",
    image: IMAGES.brandCommercialSite,
    metaTitle: "Commercial & New Construction Plumbing in WA | IDB Plumbing",
    metaDescription:
      "Commercial plumbing and new-construction packages across Washington: tenant improvements, restaurants, multi-family, and ground-up builds by licensed crews.",
    excerpt:
      "From tenant improvements to ground-up builds, our commercial crews deliver code-right plumbing on the schedule your project demands.",
    intro: [
      "Commercial plumbing is a different discipline. The stakes are schedules, inspections, and occupancy dates; the drawings are bigger, the coordination is constant, and a missed milestone costs real money down the whole chain. IDB Plumbing Solutions runs dedicated commercial crews across Washington from our Federal Way headquarters, led by plumbers who have spent their careers on plan-and-spec work and who treat your construction schedule as a commitment, not a suggestion.",
      "On new construction, we deliver the complete plumbing package: underground rough-in, waste and vent systems, domestic water distribution, gas piping, fixture setting, and final trim. We work from your drawings, flag conflicts early — before they're cast in concrete — and coordinate with the other trades so the critical path keeps moving. Our estimators produce clear, itemized bids, and our project leads communicate in the language GCs actually need: what's done, what's next, what we need from you, and when.",
      "Tenant improvements and remodels are a core strength. Restaurants are a specialty — grease interceptors, floor sinks, gas distribution to the cook line, and the health-department requirements that surprise first-time operators. We also handle medical and dental suites with their specialized fixture and backflow requirements, retail build-outs, office conversions, and multi-family projects from townhomes to mid-rise.",
      "Beyond construction, we keep commercial buildings running: scheduled backflow testing, hydro jetting programs for restaurant drain lines, water heater and boiler service, and priority emergency response for property managers who can't afford to wait in a residential queue. One number, one company, statewide coverage — and documentation for every visit that keeps your compliance files complete.",
    ],
    signs: [
      "You're bidding a project and need a reliable plumbing sub",
      "A tenant improvement requires relocated or added plumbing",
      "Your restaurant needs grease interceptor work or health-code fixes",
      "Backflow assemblies are due for annual testing",
      "Recurring drain problems are disrupting your tenants or kitchen",
      "Your property portfolio needs a priority-response service partner",
    ],
    process: [
      {
        title: "Bid from your drawings",
        text: "Clear, itemized proposals with realistic timelines — and early flags on plan conflicts that would cost you later.",
      },
      {
        title: "Coordinate and rough-in",
        text: "Underground, waste and vent, water, and gas installed to spec, sequenced with your other trades and inspected on schedule.",
      },
      {
        title: "Trim and commission",
        text: "Fixtures set, systems flushed and tested, backflow assemblies certified, and punch lists closed fast.",
      },
      {
        title: "Support after occupancy",
        text: "Maintenance programs, backflow testing, and priority emergency response keep the building running after handover.",
      },
    ],
    faqs: [
      {
        q: "What size commercial projects do you take?",
        a: "From single-suite tenant improvements to multi-family and ground-up commercial buildings. With crews in Federal Way, Vancouver, and Spokane, we scale to the project and the geography.",
      },
      {
        q: "Are you set up for public and prevailing-wage work?",
        a: "We're licensed, bonded, and insured for commercial work statewide and can discuss prevailing-wage and bonding requirements for your specific project during bidding.",
      },
      {
        q: "Do you do restaurant plumbing?",
        a: "Extensively. Grease interceptors, floor sinks, gas lines to the cook line, and health-department compliance are everyday work for our commercial crews — including scheduled jetting programs after opening.",
      },
      {
        q: "Can you test our backflow assemblies?",
        a: "Yes. We test, repair, and replace backflow prevention assemblies and file the reports your water purveyor requires, on a schedule you don't have to think about.",
      },
      {
        q: "Do you offer service contracts for property managers?",
        a: "Yes. Our commercial service agreements include priority emergency dispatch, scheduled maintenance, and consolidated reporting across your portfolio — one contact for every building, statewide.",
      },
    ],
  },
];

/** Dev-time guard: the light index in ./services-index.js must list exactly these services. */
assertIndexInSync("SERVICE_INDEX (src/data/services-index.js)", SERVICE_INDEX, SERVICES, ["slug", "name", "shortName"]);

export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);
